import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { openPrintWindow } from "@/utils/printHelper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Plus, Search, Eye, Printer, Trash2, RefreshCw, Building2, Calendar, Edit, Upload, Image, ChevronLeft, ChevronRight, FileDown, Archive, Lock, Boxes } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PasswordInput } from "@/components/ui/password-input";
import { useSubscription } from "@/context/SubscriptionContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/company-logo.png";

const StorePOList = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  
  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusPO, setStatusPO] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusComment, setStatusComment] = useState("");
  
  // Signature dialog
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signaturePO, setSignaturePO] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");
  const signatureInputRef = useRef(null);
  
  // Pagination states
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Export and Archive states
  const [isExporting, setIsExporting] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archivePassword, setArchivePassword] = useState("");
  const [archiveDays, setArchiveDays] = useState(365);
  const [isArchiving, setIsArchiving] = useState(false);

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-orders`);
      setPurchaseOrders(response.data);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPO) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-orders/${selectedPO.id}`);
      toast.success("Purchase Order deleted");
      setDeleteDialogOpen(false);
      setSelectedPO(null);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error("Failed to delete Purchase Order");
    }
  };

  // Export to Excel function
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await axios.get(`${BACKEND_URL}/api/export/purchase-orders`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `purchase_orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Purchase orders exported successfully!");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export purchase orders");
    } finally {
      setIsExporting(false);
    }
  };

  // Archive function (password protected)
  const handleArchive = async () => {
    if (!archivePassword) {
      toast.error("Please enter password");
      return;
    }
    
    try {
      setIsArchiving(true);
      const response = await axios.post(`${BACKEND_URL}/api/archive/purchase-orders`, {
        password: archivePassword,
        days_old: archiveDays,
        collection: "purchase_orders"
      });
      
      toast.success(response.data.message);
      setIsArchiveDialogOpen(false);
      setArchivePassword("");
      fetchPurchaseOrders();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid password");
      } else {
        toast.error("Failed to archive data");
      }
    } finally {
      setIsArchiving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0).replace('₹', '₹ ');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "received": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/50";
      case "sent": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "cancelled": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "rejected": return "bg-orange-500/20 text-orange-400 border-orange-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  // Status options
  const statusOptions = ["sent", "received", "cancelled", "rejected"];
  const requiresComment = (status) => status === "cancelled" || status === "rejected";

  // Open status update dialog
  const openStatusDialog = (po) => {
    setStatusPO(po);
    setNewStatus(po.status || "draft");
    setStatusComment(po.status_comment || "");
    setStatusDialogOpen(true);
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!statusPO) return;
    
    if (requiresComment(newStatus) && !statusComment.trim()) {
      toast.error("Comment is mandatory for cancelled or rejected status");
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/purchase-orders/${statusPO.id}`, {
        status: newStatus,
        status_comment: statusComment
      });
      toast.success("Status updated successfully");
      setStatusDialogOpen(false);
      setStatusPO(null);
      setStatusComment("");
      fetchPurchaseOrders();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Open signature dialog
  const openSignatureDialog = (po) => {
    setSignaturePO(po);
    setSignatureFile(null);
    setSignaturePreview(po.signature_url || "");
    setSignatureDialogOpen(true);
  };

  // Handle signature file select
  const handleSignatureSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setSignatureFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle signature upload
  const handleSignatureUpload = async () => {
    if (!signaturePO) return;
    
    try {
      // For now, store as base64 in the PO
      await axios.put(`${BACKEND_URL}/api/purchase-orders/${signaturePO.id}`, {
        signature_url: signaturePreview
      });
      toast.success("Signature uploaded successfully");
      setSignatureDialogOpen(false);
      setSignaturePO(null);
      fetchPurchaseOrders();
    } catch (error) {
      toast.error("Failed to upload signature");
    }
  };

  // Filter purchase orders
  const filteredPOs = purchaseOrders.filter(po => {
    const matchesSearch = 
      po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.vendor_company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || po.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const totalPOs = purchaseOrders.length;
  const draftPOs = purchaseOrders.filter(po => !po.status || po.status === "draft").length;
  const sentPOs = purchaseOrders.filter(po => po.status === "sent").length;
  const receivedPOs = purchaseOrders.filter(po => po.status === "received").length;
  const totalValue = purchaseOrders.reduce((sum, po) => sum + (po.grand_total || 0), 0);

  // Print PO
  const handlePrint = (po) => {

    const companyInfo = {
      name: "CHALLENGE PACKAGING INDUSTRIES",
      location: "Tamil Nadu, India",
      phone: "7299808164",
      email: "accounts@challengepack.in",
      gstin: "33AGSPV7578C2ZP"
    };

    // Line items with item name + description
    const lineItemsHTML = (po.items || po.line_items || []).map((item, idx) => {
      const itemDisplay = item.selectedItem 
        ? `<strong>${item.selectedItem}</strong>${item.description && item.description !== item.selectedItem ? `<br><span style="font-size: 12px; color: #6b7280;">${item.description}</span>` : ''}`
        : (item.description || '-');
      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${idx + 1}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${itemDisplay}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.hsn || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${item.qty ? `${item.qty} ${item.unit}` : '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${item.rate || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: 600;">${formatCurrency(item.amount)}</td>
        </tr>
      `;
    }).join('');

    // Default delivery address when not enabled
    const defaultDeliveryAddress = "Challenge Packaging Industries, Sfno - 332/1 A, Sri Visalakshi Nagar, Mettukuppam Main, Vanagaram, Chennai - 600 095 (Opp to Captain TV Tasmac)";

    const deliverySectionHTML = po.enable_delivery ? `
      <div class="info-box">
        <h3>Deliver To</h3>
        <p>
          <span class="highlight">${po.delivery_name || 'Not Specified'}</span><br>
          ${po.delivery_address ? `${po.delivery_address}<br>` : ''}
          ${po.delivery_gstin ? `GSTIN: ${po.delivery_gstin}` : ''}
        </p>
      </div>
    ` : `
      <div class="info-box">
        <h3>Deliver To</h3>
        <p>
          <span class="highlight">Challenge Packaging Industries</span><br>
          ${defaultDeliveryAddress}
        </p>
      </div>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order - ${po.po_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            padding: 20px; 
            color: #1f2937; 
            background: #fff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { padding: 0; }
            .po-container { border: none; box-shadow: none; }
            .po-header {
              background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%) !important;
              -webkit-print-color-adjust: exact !important;
            }
            .items-table th {
              background: #FEF3C7 !important;
              -webkit-print-color-adjust: exact !important;
            }
            .totals-section {
              background: #f9fafb !important;
              -webkit-print-color-adjust: exact !important;
            }
            .total-row.grand {
              background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%) !important;
              -webkit-print-color-adjust: exact !important;
            }
            .footer {
              background: #FEF3C7 !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
          .po-container { max-width: 800px; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
          .po-header { background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%); color: white; padding: 24px; display: flex; justify-content: space-between; align-items: center; }
          .company-section { display: flex; align-items: center; gap: 16px; }
          .company-logo { width: 60px; height: 60px; border-radius: 8px; background: white; padding: 4px; }
          .company-name { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
          .company-details { font-size: 11px; opacity: 0.9; line-height: 1.5; }
          .po-title { text-align: right; }
          .po-title h1 { font-size: 28px; font-weight: 700; margin-bottom: 4px; }
          .po-number { font-size: 14px; background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 4px; display: inline-block; }
          .info-section { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; padding: 20px 24px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
          .info-box { padding: 16px; background: white; border-radius: 8px; border: 1px solid #e5e7eb; }
          .info-box h3 { font-size: 11px; text-transform: uppercase; color: #6b7280; margin-bottom: 8px; letter-spacing: 0.5px; }
          .info-box p { font-size: 13px; line-height: 1.6; }
          .info-box .highlight { font-weight: 600; color: #D97706; }
          .items-table { width: 100%; border-collapse: collapse; }
          .items-table th { background: #FEF3C7; padding: 14px 12px; text-align: left; font-size: 11px; text-transform: uppercase; color: #92400E; letter-spacing: 0.5px; border-bottom: 2px solid #F59E0B; }
          .items-table th:nth-child(1) { text-align: center; width: 40px; }
          .items-table th:nth-child(3) { text-align: center; }
          .items-table th:nth-child(4), .items-table th:nth-child(5), .items-table th:nth-child(6) { text-align: right; }
          .totals-section { display: flex; justify-content: flex-end; padding: 20px 24px; background: #f9fafb; }
          .totals-box { width: 300px; }
          .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
          .total-row.grand { border-bottom: none; background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%); color: white; margin: 12px -16px -16px; padding: 16px; border-radius: 0 0 8px 8px; font-size: 18px; font-weight: 700; }
          .signature-section { padding: 30px 24px; text-align: right; border-top: 1px solid #e5e7eb; }
          .signature-box { display: inline-block; text-align: center; }
          .signature-line { width: 200px; border-bottom: 2px solid #1f2937; margin-bottom: 8px; height: 60px; }
          .signature-label { font-size: 12px; color: #6b7280; }
          .footer { text-align: center; padding: 16px; background: #FEF3C7; font-size: 11px; color: #92400E; }
          @media print { body { padding: 0; } .po-container { border: none; } }
        </style>
      </head>
      <body>
        <div class="po-container">
          <div class="po-header">
            <div class="company-section">
              <img src="${LOGO_URL}" alt="CPI Logo" class="company-logo" />
              <div>
                <div class="company-name">${companyInfo.name}</div>
                <div class="company-details">
                  ${companyInfo.location}<br>
                  Phone: ${companyInfo.phone} | Email: ${companyInfo.email}<br>
                  GSTIN: ${companyInfo.gstin}
                </div>
              </div>
            </div>
            <div class="po-title">
              <h1>Purchase Order</h1>
              <span class="po-number">${po.po_number}</span>
            </div>
          </div>
          
          <div class="info-section">
            <div class="info-box">
              <h3>Vendor</h3>
              <p>
                <span class="highlight">${po.vendor_company_name || 'Not Specified'}</span><br>
                ${po.vendor_name ? `Contact: ${po.vendor_name}<br>` : ''}
                ${po.vendor_address ? `${po.vendor_address}<br>` : ''}
                ${po.vendor_gstin ? `GSTIN: ${po.vendor_gstin}` : ''}
              </p>
            </div>
            ${deliverySectionHTML}
            <div class="info-box">
              <h3>PO Details</h3>
              <p>
                <strong>PO Date:</strong> ${po.po_date}<br>
                <strong>Delivery Date:</strong> ${po.delivery_date || 'TBD'}
              </p>
            </div>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item & Description</th>
                <th>HSN/SAC</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lineItemsHTML}
            </tbody>
          </table>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="total-row">
                <span>Sub Total</span>
                <span>${formatCurrency(po.sub_total)}</span>
              </div>
              <div class="total-row">
                <span>CGST (${po.cgst_rate}%)</span>
                <span>${formatCurrency(po.cgst_amount)}</span>
              </div>
              <div class="total-row">
                <span>SGST (${po.sgst_rate}%)</span>
                <span>${formatCurrency(po.sgst_amount)}</span>
              </div>
              <div class="total-row grand">
                <span>Grand Total</span>
                <span>${formatCurrency(po.grand_total)}</span>
              </div>
            </div>
          </div>
          
          <div class="signature-section">
            <div class="signature-box">
              ${po.signature_url ? `<img src="${po.signature_url}" alt="Signature" style="max-width: 200px; max-height: 60px; object-fit: contain;" />` : '<div class="signature-line"></div>'}
              <div class="signature-label">Authorized Signature</div>
            </div>
          </div>
          
          <div class="footer">
            Challenge Packaging Industries - Purchase Order
          </div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    openPrintWindow(htmlContent);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-amber-400 tracking-wider flex items-center gap-3">
            <FileText className="h-8 w-8" />
            Purchase Orders
          </h1>
          <p className="text-gray-400 mt-1">Manage and track purchase orders</p>
        </div>
        <div className="flex gap-2">
          {hasFeature("inventory_control") && (
            <Button
              onClick={() => navigate("/store/inventory-control")}
              variant="outline"
              className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20"
              data-testid="po-list-open-inventory-control"
            >
              <Boxes className="h-4 w-4 mr-2" />
              Inventory Control
            </Button>
          )}
          <Button
            onClick={fetchPurchaseOrders}
            variant="outline"
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => navigate("/store/purchase-order/new")}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New PO
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card className="bg-black/60 border-2 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <FileText className="h-8 w-8 text-amber-400 mb-2" />
              <p className="text-2xl font-black text-amber-400">{totalPOs}</p>
              <p className="text-xs text-gray-400">Total POs</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-gray-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <p className="text-2xl font-black text-gray-400">{draftPOs}</p>
              <p className="text-xs text-gray-400">Draft</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <p className="text-2xl font-black text-blue-400">{sentPOs}</p>
              <p className="text-xs text-gray-400">Sent</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <p className="text-2xl font-black text-emerald-400">{receivedPOs}</p>
              <p className="text-xs text-gray-400">Received</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-black/60 border-2 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex flex-col items-center text-center">
              <p className="text-2xl font-black text-purple-400">{formatCurrency(totalValue).split('.')[0]}</p>
              <p className="text-xs text-gray-400">Total Value</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="bg-black/60 border-2 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by PO number or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-black/50 border-amber-500/30 text-white"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] bg-black/50 border-amber-500/30 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-amber-500/30">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* PO List Table */}
      <Card className="bg-black/60 border-2 border-amber-500/30">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-amber-400">Purchase Orders ({filteredPOs.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20 h-8"
                onClick={handleExportToExcel}
                disabled={isExporting}
              >
                <FileDown className={`h-4 w-4 mr-1 ${isExporting ? 'animate-pulse' : ''}`} />
                Export
              </Button>
              <Button
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20 h-8"
                onClick={() => setIsArchiveDialogOpen(true)}
              >
                <Archive className="h-4 w-4 mr-1" />
                Archive
              </Button>
              <Label className="text-gray-400 text-sm">Show:</Label>
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-8 bg-black/50 border-amber-500/30 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-amber-500/30">
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-gray-400 text-sm">entries</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading purchase orders...</div>
          ) : filteredPOs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-600" />
              <p className="text-xl font-bold text-gray-400 mb-2">No Purchase Orders</p>
              <p className="text-gray-500 mb-4">Create your first purchase order to get started</p>
              <Button
                onClick={() => navigate("/store/purchase-order/new")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New PO
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-amber-500/30 hover:bg-transparent">
                      <TableHead className="text-amber-300">PO Number</TableHead>
                      <TableHead className="text-amber-300">Vendor</TableHead>
                      <TableHead className="text-amber-300">PO Date</TableHead>
                      <TableHead className="text-amber-300">Delivery Date</TableHead>
                      <TableHead className="text-amber-300 text-right">Grand Total</TableHead>
                      <TableHead className="text-amber-300 text-center">Status</TableHead>
                      <TableHead className="text-amber-300 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPOs.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((po) => (
                    <TableRow key={po.id} className="border-amber-500/20 hover:bg-amber-500/10">
                      <TableCell className="font-mono font-bold text-white">{po.po_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-amber-400" />
                          <span className="text-gray-300">{po.vendor_company_name || "-"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          {po.po_date}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300">{po.delivery_date || "-"}</TableCell>
                      <TableCell className="text-right font-bold text-amber-400">
                        {formatCurrency(po.grand_total)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusDialog(po)}
                          className="p-0 h-auto"
                        >
                          <span className={cn("px-2 py-1 text-xs rounded-full border cursor-pointer hover:opacity-80", getStatusColor(po.status))}>
                            {po.status?.toUpperCase() || "DRAFT"}
                          </span>
                        </Button>
                        {po.status_comment && (requiresComment(po.status)) && (
                          <p className="text-xs text-gray-500 mt-1 max-w-[100px] truncate" title={po.status_comment}>
                            {po.status_comment}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/store/purchase-order/new?edit=${po.id}`)}
                            className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                            title="Edit PO"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openStatusDialog(po)}
                            className="text-purple-400 hover:text-purple-300 h-8 w-8 p-0"
                            title="Update Status"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openSignatureDialog(po)}
                            className="text-cyan-400 hover:text-cyan-300 h-8 w-8 p-0"
                            title="Add Signature"
                          >
                            <Image className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePrint(po)}
                            className="text-amber-400 hover:text-amber-300 h-8 w-8 p-0"
                            title="Print"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedPO(po);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-amber-500/20">
                <div className="text-sm text-gray-400">
                  Showing {Math.min((currentPage - 1) * pageSize + 1, filteredPOs.length)} to {Math.min(currentPage * pageSize, filteredPOs.length)} of {filteredPOs.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                  </Button>
                  <span className="text-amber-400 px-3">
                    Page {currentPage} of {Math.ceil(filteredPOs.length / pageSize) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredPOs.length / pageSize), p + 1))}
                    disabled={currentPage >= Math.ceil(filteredPOs.length / pageSize)}
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Purchase Order</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete PO "{selectedPO?.po_number}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-500 text-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Update PO Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-amber-300">PO Number</Label>
              <p className="text-white font-mono font-bold">{statusPO?.po_number}</p>
            </div>
            <div>
              <Label className="text-amber-300">Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-black/50 border-amber-500/30 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-amber-500/30">
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {requiresComment(newStatus) && (
              <div>
                <Label className="text-amber-300">
                  Comment <span className="text-red-400">*</span>
                  <span className="text-gray-500 text-xs ml-2">(Required for {newStatus})</span>
                </Label>
                <Textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  className="bg-black/50 border-amber-500/30 text-white mt-1"
                  placeholder={`Enter reason for ${newStatus}...`}
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Upload Dialog */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="bg-gray-900 border-cyan-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <Image className="h-5 w-5" />
              Add Authorized Signature
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-cyan-300">PO Number</Label>
              <p className="text-white font-mono font-bold">{signaturePO?.po_number}</p>
            </div>
            <div>
              <Label className="text-cyan-300">Upload Signature Image</Label>
              <div className="mt-2 flex flex-col items-center gap-4">
                <input
                  ref={signatureInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => signatureInputRef.current?.click()}
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Image
                </Button>
                {signaturePreview && (
                  <div className="w-full p-4 bg-white rounded-lg">
                    <img 
                      src={signaturePreview} 
                      alt="Signature Preview" 
                      className="max-w-full max-h-32 mx-auto object-contain"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Supported formats: PNG, JPG, JPEG. Max size recommended: 500KB
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureDialogOpen(false)} className="border-gray-500 text-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleSignatureUpload} 
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              disabled={!signaturePreview}
            >
              Save Signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog (Password Protected) */}
      <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-xl flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Archive Old Purchase Orders
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm flex items-center gap-2">
                <Lock className="h-4 w-4" />
                This action will archive (move to backup) old purchase orders.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Archive POs older than (days)</Label>
              <Input
                type="number"
                value={archiveDays}
                onChange={(e) => setArchiveDays(parseInt(e.target.value) || 365)}
                className="bg-black/50 border-red-500/30 text-white mt-1"
                min={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                POs dated before {new Date(Date.now() - archiveDays * 24 * 60 * 60 * 1000).toLocaleDateString()} will be archived
              </p>
            </div>
            
            <div>
              <Label className="text-gray-300">Enter Password to Confirm</Label>
              <PasswordInput
                value={archivePassword}
                onChange={(e) => setArchivePassword(e.target.value)}
                className="bg-black/50 border-red-500/30 text-white mt-1"
                placeholder="Enter archival password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setIsArchiveDialogOpen(false); setArchivePassword(""); }}
              className="border-gray-500 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleArchive}
              disabled={isArchiving || !archivePassword}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isArchiving ? "Archiving..." : "Archive Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorePOList;
